import { useState, useEffect } from 'react';
import type { UserJob, SubcomponentType } from '../types';

interface UseJobPollingParams {
  jobs: UserJob[];
  loadJobs: (isBackgroundRefresh?: boolean) => Promise<void>;
}

interface UseJobPollingResult {
  generatingTracking: Map<string, number>;
  setGeneratingTracking: React.Dispatch<React.SetStateAction<Map<string, number>>>;
}

export default function useJobPolling({
  jobs,
  loadJobs,
}: UseJobPollingParams): UseJobPollingResult {
  const [generatingTracking, setGeneratingTracking] = useState<Map<string, number>>(new Map());
  const [pageLoadTime] = useState<number>(Date.now());

  // Track generation start times and detect stale requests
  useEffect(() => {
    const now = Date.now();
    const TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes
    const subcomponents: SubcomponentType[] = ['contact', 'summary', 'skills', 'highlights', 'experience', 'education', 'awards', 'presentations', 'publications', 'portfolios', 'coverletter'];

    jobs.forEach((job) => {
      subcomponents.forEach(comp => {
        const state = job[`state${comp}` as keyof UserJob] as string;
        const trackingKey = `${job.jobid}-${comp}`;

        if (state === 'generating') {
          // Track when generation started
          if (!generatingTracking.has(trackingKey)) {
            setGeneratingTracking(prev => new Map(prev).set(trackingKey, now));
          } else {
            // Check if it's been too long
            const startTime = generatingTracking.get(trackingKey);
            if (startTime && (now - startTime) > TIMEOUT_MS) {
              console.warn(`Generation timeout detected for ${job.jobid} ${comp} - started ${Math.round((now - startTime) / 1000)}s ago`);
              // Remove from tracking - it's considered stale
              setGeneratingTracking(prev => {
                const next = new Map(prev);
                next.delete(trackingKey);
                return next;
              });
            }
          }
        } else {
          // Clear tracking if not generating
          if (generatingTracking.has(trackingKey)) {
            setGeneratingTracking(prev => {
              const next = new Map(prev);
              next.delete(trackingKey);
              return next;
            });
          }
        }
      });
    });
  }, [jobs, generatingTracking]);

  // Poll for updates when jobs are generating (excluding stale requests)
  useEffect(() => {
    const now = Date.now();
    const TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes
    const INITIAL_TIMEOUT_MS = 2 * 60 * 1000; // 2 minutes for states that were already generating on page load

    const hasActiveGenerating = jobs.some((job) => {
      // Check if job phase is Generating
      if (job.jobphase === 'Generating') return true;

      const subcomponents: SubcomponentType[] = ['contact', 'summary', 'skills', 'highlights', 'experience', 'education', 'awards', 'presentations', 'publications', 'portfolios', 'coverletter'];
      return subcomponents.some(comp => {
        const state = job[`state${comp}` as keyof UserJob] as string;
        if (state !== 'generating') return false;

        // Check if it's a stale request
        const trackingKey = `${job.jobid}-${comp}`;
        const startTime = generatingTracking.get(trackingKey);

        if (!startTime) {
          // This state was already "generating" when we first saw it
          // Only poll for it if it's within 2 minutes of page load
          // (assumes requests from before page load are likely stale)
          const timeSincePageLoad = now - pageLoadTime;
          if (timeSincePageLoad > INITIAL_TIMEOUT_MS) {
            console.warn(`Ignoring stale generating state for ${job.jobid} ${comp} - was already generating on page load`);
            return false;
          }
        } else if ((now - startTime) > TIMEOUT_MS) {
          // Request started during this session but has exceeded timeout
          return false;
        }
        return true;
      });
    });

    if (hasActiveGenerating) {
      const interval = setInterval(() => loadJobs(true), 2000); // Poll every 2 seconds as background refresh
      return () => clearInterval(interval);
    }
  }, [jobs, loadJobs, generatingTracking, pageLoadTime]);

  // 30-second background polling for general data freshness
  useEffect(() => {
    const POLL_INTERVAL_MS = 30_000;
    const interval = setInterval(() => {
      // Only poll when tab is visible
      if (!document.hidden) {
        loadJobs(true);
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [loadJobs]);

  return { generatingTracking, setGeneratingTracking };
}
